import { applyPalette } from '../core/state-helpers';
import type { MosaicParams, PatternParams, PatternState } from '../types';
import { DEFAULT_PALETTE } from './palettes';

// Halftone defaults — Media Studio Image tab (morfi-media-studio_4.html).
export const DEFAULT_PARAMS: PatternParams = {
  imgMode: 'dark',
  imgContrast: 1.1,
  imgCutoff: 96,
  spacing: 15,
  minDot: 16,
  maxDot: 82,
  intensity: 0.71,
  gridAngle: 34,
  coverage: 0.1,
  falloff: 1.35,
  baseFill: 1,
  gridType: 'square',
  strokeWeight: 1,
  cropAnchorX: 0.5,
};

// Mosaic defaults — avatars only. Values mirror the prior locked mosaic look.
export const DEFAULT_MOSAIC_PARAMS: MosaicParams = {
  cellSize: 200,
  mBrightness: 0,
  mContrast: 1.55,
  mInvert: 0,
  mThreshold: 1,
  dotScale: 0.37,
};

export const DEFAULT_STATE: PatternState = {
  renderer: 'halftone',
  palette: DEFAULT_PALETTE,
  bg: DEFAULT_PALETTE[0] ?? '#000000',
  seed: 1337,
  params: DEFAULT_PARAMS,
  mosaicParams: DEFAULT_MOSAIC_PARAMS,
  image: {
    loaded: false,
    shape: 'full',
    sampled: null,
  },
  mosaic: {
    colorMode: 'original',
    dither: false,
    edgeMode: 'off',
    edgeDepth: 5,
    edgeRoughness: 0.5,
  },
};

/**
 * Build a `PatternState` from `DEFAULT_STATE` plus optional overrides.
 * If `overrides.palette` is provided, `bg` is auto-pinned to its dimmest
 * stop (via `applyPalette`) — the lib's canonical bg-sync convention.
 */
export function createPatternState(overrides: Partial<PatternState> = {}): PatternState {
  const merged: PatternState = { ...DEFAULT_STATE, ...overrides };
  if (overrides.palette !== undefined) {
    return applyPalette(merged, overrides.palette);
  }
  return merged;
}
